import { Grid } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import StreamFailed from "../Wowza/utils/StreamFailed";
import { CircularProgress } from "@mui/material";
import utils from "./utils";

const VicadsLiveWebRTCPlayerWrapper = ({ camera, instanceId, containerRef, updateDimensions, isInFullScreen }) => {
	return (
		<VicadsLiveWebRTCPlayer
			key={`${camera.id}-${isInFullScreen}`}
			camera={camera}
			instanceId={instanceId}
			containerRef={containerRef}
			isInFullScreen={isInFullScreen}
			updateDimensions={updateDimensions}
		/>
	);
};

const VicadsLiveWebRTCPlayer = ({ camera, instanceId, containerRef, updateDimensions, isInFullScreen }) => {
	const videoElemRef = useRef(null);
	const playPendingRef = useRef(true);
	const [token, setToken] = useState("");
	const [videoWidth, setVideoWidth] = useState(0);
	const [videoHeight, setVideoHeight] = useState(0);
	const isNewVideoPlayer = camera.player?.newPlayer || false;
	const [isLoaded, setIsLoaded] = useState(false);
	const [streamStatus, setStreamStatus] = useState("Starting");
	const [vicadsHost, setVicadsHost] = useState("");

	useEffect(() => {
		return () => {
			clearVideo();
		};
	}, []);

	useEffect(() => {
		if (camera?.player?.host) {
			setVicadsHost(camera?.player?.host);
		}
	}, [camera]);

	useEffect(() => {
		const setAuthToken = authToken => {
			setToken(authToken);
			if (authToken === "") {
				setIsLoaded(false);
				setStreamStatus("Failed");
				return;
			}
			videoElemRef.current.vicadsauth = authToken;
		};
		utils.registerTokenSetter(instanceId, camera, setAuthToken);

		return () => {
			utils.unregisterTokenSetter(instanceId, camera);
		};
	}, []);

	useEffect(() => {
		setVideoWidth(containerRef.current?.offsetWidth);
		setVideoHeight(containerRef.current?.offsetHeight);
		minimizedVideoViewSettings();
	}, [updateDimensions, containerRef]);

	useEffect(() => {
		if (isInFullScreen) {
			let dimensions = null;
			dimensions = utils.get16by9Dimensions(window.innerWidth, window.innerHeight);
			if (dimensions) {
				setVideoWidth(dimensions.width);
				setVideoHeight(dimensions.height);
			}
		}
	}, [isInFullScreen]);

	const minimizedVideoViewSettings = () => {
		if (!isInFullScreen) {
			if (videoElemRef.current !== null && (containerRef.current || (videoWidth && videoHeight))) {
				videoElemRef.current.videowidth = containerRef.current?.offsetWidth || videoWidth;
				videoElemRef.current.videoheight = containerRef.current?.offsetHeight || videoHeight;
			}
		}
	};

	//The following useEffect hook is responsible for shrinking the video player without affecting the video quality set in the previous useEffect. In essence, if the video player is not in fullscreen mode, the container's width and height passed to this component will be used. Otherwise, the window's dimensions will be used.
	useEffect(() => {
		if (isInFullScreen) {
			if (videoElemRef.current !== null) {
				const dimensions = utils.get16by9Dimensions(window.innerWidth, window.innerHeight);
				if (dimensions) {
					videoElemRef.current.videowidth = dimensions.width;
					videoElemRef.current.videoheight = dimensions.height;
				}
			}
		}
	}, [videoElemRef, isInFullScreen]);

	useEffect(() => {
		if (playPendingRef.current && token && videoWidth && videoHeight) {
			if (videoElemRef.current !== null) {
				videoElemRef.current.videowidth = videoWidth;
				videoElemRef.current.videoheight = videoHeight;
				const videoDivElement = videoElemRef.current.querySelector(".videoDiv");
				if (videoDivElement !== null) {
					const videoChildElement = videoDivElement.querySelector("video");
					videoChildElement.style.top = "0px";
				}
			}
			playVideo();
			playPendingRef.current = false;
		}
	}, [token, videoWidth, videoHeight]);

	const getPlayerInfoHandler = event => {
		console.log("Video player Info:", event.detail);
		if (event.detail === "playing") {
			setIsLoaded(true);
			setStreamStatus("Success");
			minimizedVideoViewSettings();
		}
		if (event.detail === "WebSocket error" || event.detail.includes("Viewing error")) {
			//handle loaders and fall back
			setIsLoaded(false);
			setStreamStatus("Failed");
		}
	};

	const getPlayerErrorHandler = event => {
		console.log("Error:", event.detail);
	};

	// This useEffect is responsible for listening to changes in the video player.
	// If the video starts streaming successfully, it will stop the loader and display the actual video content.
	// If not, an error message with a retry button will be displayed on the player.
	useEffect(() => {
		const videoElement = videoElemRef.current;

		if (videoElement) {
			videoElement.addEventListener("info", getPlayerInfoHandler);
			videoElement.addEventListener("error", getPlayerErrorHandler);
		}
		return () => {
			// Remove event listeners when the component unmounts
			videoElement.removeEventListener("info", getPlayerInfoHandler);
			videoElement.removeEventListener("error", getPlayerErrorHandler);
		};
	}, [videoElemRef]);

	const clearVideo = () => {
		const videoElem = videoElemRef.current;
		if (!videoElem) return;
		videoElem.playvideo = "";
	};

	// This function is called when the vicads-video element gets an auth property during login.
	// This sets the playvideo property of vicads-video with the captured video ID, initiating WebSocket
	// and other background processes to stream the video.
	const playVideo = () => {
		const videoElem = videoElemRef.current;
		if (!videoElem) return;
		const videoId = camera.connection.cameraId;

		videoElem.playvideo = `live?videoUID=${videoId}&hilo=hi${camera?.player?.additionalParams ? camera?.player?.additionalParams : ""}`;
	};

	const retryStream = () => {
		clearVideo();
		setTimeout(() => {
			playVideo();
			setIsLoaded(false);
			setStreamStatus("Starting");
		}, 500);
	};

	//  This function is used to display a loader initially until the WebSocket connection completes and starts streaming video.
	//  If there is an issue with the connection, it will display a fallback message on the video player,
	// allowing us to retry connecting to the WebSocket.
	const streamFallbackHandler = () => {
		if (streamStatus === "Starting" || streamStatus === "Failed") {
			return (
				<div
					style={{
						position: "absolute",
						height: "100%",
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center"
					}}
				>
					{streamStatus === "Starting" && <CircularProgress />}
					{streamStatus === "Failed" && <StreamFailed retryStream={retryStream} />}
				</div>
			);
		}

		return null;
	};

	return (
		<Grid
			container
			spacing={isNewVideoPlayer ? 0 : 3}
			sx={{ ...(isLoaded && { width: "100%", height: "100%" }) }}
			className="mt-3"
		>
			{!isLoaded && streamFallbackHandler()}
			<div
				style={{
					display: "flex",
					opacity: isLoaded && vicadsHost !== "" ? 1 : 0,
					width: "100%",
					height: "100%",
					justifyContent: "center"
				}}
			>
				{/* This vicads-video element is from the vicads main.js file fetched from MinIO */}
				<vicads-video
					id={"VicadsVideoPlayer" + instanceId}
					videoTag={"video" + instanceId}
					vicadsHost={vicadsHost}
					viewerconfig='{ "minBandwidth": 4096, "maxBandwidth": 12288 }'
					ref={videoElemRef}
				></vicads-video>
			</div>
		</Grid>
	);
};

export default VicadsLiveWebRTCPlayerWrapper;
